import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  Input,
  OnInit,
} from '@angular/core';
import { faThumbsDown, faThumbsUp } from '@fortawesome/free-regular-svg-icons';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { get } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { Observable, of, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BrowserControlService } from '../../cross-team-shared/misc-services/browser-control.service';
import { EventTrackingService } from '../../shared/event-tracking/event-tracking.service';
import { IndividualFieldScore } from '../../team-apis/otterdam/individual-field-score';
import { Output } from '../../team-apis/otterdam/outputs/output';
import { FeedbackPersistenceService } from '../feedback/services/feedback-persistence.service';
import { getValuesForOutputContext } from '../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../h3ml/state/task-workspace-store';
import { SamplingWorkspaceStore } from '../sampling-workspace/state/sampling-workspace-store';
import { FeatureFlagService } from '../shared/misc-services/feature-flag.service';

@Component({
  selector: 'h3-individual-field-scores',
  templateUrl: './individual-field-scores.component.html',
  styleUrls: ['./individual-field-scores.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IndividualFieldScoresComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input({ required: true }) output!: Output;
  scoreStore?: Store<IndividualFieldScore>;
  editable = this.taskWorkspaceStore.state().qaFeedbackIsEditable;
  shouldBeVisible$!: Observable<boolean>;
  errorTagsCount$?: Observable<number>;
  faThumbsUp = faThumbsUp;
  faThumbsDown = faThumbsDown;
  hasErrors$ = of(false);
  private feedbackPersistenceService = inject(FeedbackPersistenceService);
  private featureFlagService = inject(FeatureFlagService);

  constructor(
    private eventTrackingService: EventTrackingService,
    private samplingWorkspaceStore: SamplingWorkspaceStore,
    private taskWorkspaceStore: TaskWorkspaceStore,
    private browserControlService: BrowserControlService,
    injector: Injector,
  ) {
    super(injector);
  }

  ngOnInit() {
    this.loadIndividualFieldScores();
    this.shouldBeVisible$ = this.buildShouldBeVisible$();

    this.subscribeTo(
      this.shouldBeVisible$.pipe(filter((shouldBeVisible) => shouldBeVisible)),
      () => {
        assert(
          this.scoreStore,
          'The scoreStore exists if the componenent is visible.',
        );
        this.errorTagsCount$ = this.scoreStore('error_tags').$.pipe(
          filter(isDefined),
          map((errorTags) => errorTags.length),
        );
      },
    );
  }

  toggleScore(score: boolean) {
    if (!this.scoreStore) {
      throw new Error("there should be a score store but there isn't");
    }

    const scoreState = this.scoreStore.state();

    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (scoreState && scoreState.score === score) {
      this.scoreStore.delete();
      this.feedbackPersistenceService.deleteFieldFeedback$(this.output.id);

      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    } else if (scoreState) {
      this.scoreStore('score').set(score);
      this.upsertFieldFeedback();
    } else {
      this.scoreStore.set({ score, error_tags: [] });
      this.upsertFieldFeedback();
    }
    this.eventTrackingService.track({
      id: 'set_individual_field_score',
      bucket: 'quality_feedback',
      score: get(this.scoreStore.state(), ['score']),
    });
  }

  async copyErrorTags() {
    const errorTags = this.scoreStore!.state().error_tags;
    const errorTagsToCopy = errorTags.join(';');
    await this.browserControlService.writeClipboardText(errorTagsToCopy);
  }

  buildShouldBeVisible$() {
    if (this.featureFlagService.hasFlag('new-sampling-nav')) {
      return of(false);
    }
    return this.taskWorkspaceStore('task')('id').$.pipe(
      switchMap(() =>
        this.taskWorkspaceStore('individualFieldScore').$.pipe(
          filter(isDefined),
        ),
      ),
      map(() => {
        if (!this.scoreStore) {
          return false;
        }
        return true;
      }),
    );
  }

  handleTagChanges(errorTags: string[]) {
    this.upsertFieldFeedback();
    this.eventTrackingService.track({
      id: 'set_error_tags',
      bucket: 'quality_feedback',
      tags: errorTags,
    });
  }

  private loadIndividualFieldScores() {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (this.output) {
      this.eventTrackingService.addContext(
        getValuesForOutputContext(this.output),
      );
      const individualFieldScoresStore = this.getIndividualFieldScoresStore();
      this.scoreStore = individualFieldScoresStore(this.output.id);
      this.hasErrors$ = this.scoreStore.$.pipe(
        map(
          (state: IndividualFieldScore | undefined) =>
            this.editable &&
            state?.score === false &&
            !!this.output.error_tags.length,
        ),
      );
    }
  }

  private getIndividualFieldScoresStore() {
    return this.taskWorkspaceStore('individualFieldScore');
  }

  private upsertFieldFeedback() {
    const individualFieldScore =
      this.taskWorkspaceStore.state().individualFieldScore[this.output.id]!;
    const sampleTask = this.samplingWorkspaceStore('master').state();
    this.feedbackPersistenceService.upsertFieldFeedback$(this.output.id, {
      sampleId: sampleTask.sample_id,
      stepId: this.taskWorkspaceStore.getLastSubmission().step_id,
      score: individualFieldScore.score ? 100 : 0,
      errorTags: individualFieldScore.error_tags,
    });
  }
}
